import React, { useState } from 'react';
import { CalculatorIcon, ArrowRightIcon } from './icons';

export default function CalculatorsPage() {
    const [activeCalc, setActiveCalc] = useState<'bmi' | 'tdee' | 'oneRM' | 'macros' | 'wilks'>('bmi');

    return (
        <div className="max-w-4xl mx-auto animate-fade-in">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-xl bg-green-100 dark:bg-green-950 flex items-center justify-center">
                    <CalculatorIcon size={22} className="text-green-600" />
                </div>
                <div>
                    <h2 className="text-lg font-bold">Kalkulatory fitness</h2>
                    <p className="text-xs text-slate-400">
                        Oblicz BMI, zapotrzebowanie kaloryczne, maksymalny ciężar i więcej
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 mb-6 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-1">
                {(['bmi', 'tdee', 'oneRM', 'macros', 'wilks'] as const).map((calc) => (
                    <button
                        key={calc}
                        onClick={() => setActiveCalc(calc)}
                        className={`px-3 py-2 rounded-lg text-xs font-semibold transition-all ${activeCalc === calc
                            ? 'bg-brand-600 text-white shadow-sm'
                            : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800'
                            }`}
                    >
                        {calc === 'bmi' && 'BMI'}
                        {calc === 'tdee' && 'Kalorie (TDEE)'}
                        {calc === 'oneRM' && '1RM'}
                        {calc === 'macros' && 'Makroskładniki'}
                        {calc === 'wilks' && 'Wilks'}
                    </button>
                ))}
            </div>

            {/* Calculator content */}
            <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-6">
                {activeCalc === 'bmi' && <BMICalculator />}
                {activeCalc === 'tdee' && <TDEECalculator />}
                {activeCalc === 'oneRM' && <OneRMCalculator />}
                {activeCalc === 'macros' && <MacroCalculator />}
                {activeCalc === 'wilks' && <WilksCalculator />}
            </div>
        </div>
    );
}

// ── Shared input component ──
function Input({ label, value, onChange, type = 'number', min, max, step, placeholder, unit }: {
    label: string; value: string | number; onChange: (v: string) => void;
    type?: string; min?: number; max?: number; step?: number; placeholder?: string; unit?: string;
}) {
    return (
        <div>
            <label className="block text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">{label}</label>
            <div className="relative">
                <input
                    type={type}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    min={min} max={max} step={step}
                    placeholder={placeholder}
                    className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                />
                {unit && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{unit}</span>
                )}
            </div>
        </div>
    );
}

function ResultCard({ label, value, color = 'brand' }: { label: string; value: string; color?: string }) {
    return (
        <div className={`p-4 rounded-xl bg-${color}-50 dark:bg-${color}-950/30 border border-${color}-200 dark:border-${color}-800 text-center`}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-xl font-black text-${color}-600 dark:text-${color}-400`}>{value}</p>
        </div>
    );
}

// ══════════════════════════════════════════
// BMI Calculator
// ══════════════════════════════════════════
function BMICalculator() {
    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');

    const bmi = weight && height ? parseFloat(weight) / Math.pow(parseFloat(height) / 100, 2) : null;

    const getCategory = (bmi: number): { label: string; color: string } => {
        if (bmi < 18.5) return { label: 'Niedowaga', color: 'text-blue-500' };
        if (bmi < 25) return { label: 'Norma', color: 'text-green-500' };
        if (bmi < 30) return { label: 'Nadwaga', color: 'text-amber-500' };
        if (bmi < 35) return { label: 'Otyłość I°', color: 'text-orange-500' };
        if (bmi < 40) return { label: 'Otyłość II°', color: 'text-red-500' };
        return { label: 'Otyłość III°', color: 'text-red-700' };
    };

    return (
        <div>
            <h3 className="font-bold text-base mb-1">Kalkulator BMI</h3>
            <p className="text-xs text-slate-400 mb-4">Body Mass Index - wskaźnik masy ciała</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
                <Input label="Waga" value={weight} onChange={setWeight} min={30} max={300} unit="kg" placeholder="80" />
                <Input label="Wzrost" value={height} onChange={setHeight} min={100} max={250} unit="cm" placeholder="180" />
            </div>

            {bmi && bmi > 0 && bmi < 100 && (
                <div className="grid grid-cols-2 gap-4 animate-fade-in">
                    <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-950/30 border border-brand-200 dark:border-brand-800 text-center">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Twoje BMI</p>
                        <p className="text-2xl font-black text-brand-600 dark:text-brand-400">{bmi.toFixed(1)}</p>
                    </div>
                    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-center flex flex-col justify-center">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">Kategoria</p>
                        <p className={`text-lg font-bold ${getCategory(bmi).color}`}>{getCategory(bmi).label}</p>
                    </div>
                </div>
            )}

            <div className="mt-6">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Skala BMI</p>
                <div className="flex rounded-lg overflow-hidden h-3 text-[8px]">
                    <div className="bg-blue-400 flex-1" title="<18.5" />
                    <div className="bg-green-400 flex-[2]" title="18.5-25" />
                    <div className="bg-amber-400 flex-1" title="25-30" />
                    <div className="bg-orange-400 flex-1" title="30-35" />
                    <div className="bg-red-500 flex-1" title="35+" />
                </div>
                <div className="flex justify-between text-[9px] text-slate-400 mt-1">
                    <span>Niedowaga</span>
                    <span>Norma</span>
                    <span>Nadwaga</span>
                    <span>Otyłość</span>
                </div>
            </div>
        </div>
    );
}

// ══════════════════════════════════════════
// TDEE Calculator (Mifflin-St Jeor)
// ══════════════════════════════════════════
function TDEECalculator() {
    const [weight, setWeight] = useState('');
    const [height, setHeight] = useState('');
    const [age, setAge] = useState('');
    const [gender, setGender] = useState<'male' | 'female'>('male');
    const [activity, setActivity] = useState('1.55');

    const w = parseFloat(weight);
    const h = parseFloat(height);
    const a = parseFloat(age);
    const act = parseFloat(activity);

    let bmr = 0;
    if (w > 0 && h > 0 && a > 0) {
        bmr = gender === 'male'
            ? 10 * w + 6.25 * h - 5 * a + 5
            : 10 * w + 6.25 * h - 5 * a - 161;
    }
    const tdee = bmr * act;

    const activityLevels = [
        { value: '1.2', label: 'Siedzący (brak ćwiczeń)' },
        { value: '1.375', label: 'Lekko aktywny (1-3×/tydz.)' },
        { value: '1.55', label: 'Umiarkowanie aktywny (3-5×/tydz.)' },
        { value: '1.725', label: 'Bardzo aktywny (6-7×/tydz.)' },
        { value: '1.9', label: 'Ekstremalnie aktywny (2×/dzień)' },
    ];

    return (
        <div>
            <h3 className="font-bold text-base mb-1">Kalkulator TDEE</h3>
            <p className="text-xs text-slate-400 mb-4">Całkowite dzienne zapotrzebowanie energetyczne (Mifflin-St Jeor)</p>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                <Input label="Waga" value={weight} onChange={setWeight} min={30} max={300} unit="kg" />
                <Input label="Wzrost" value={height} onChange={setHeight} min={100} max={250} unit="cm" />
                <Input label="Wiek" value={age} onChange={setAge} min={10} max={100} unit="lat" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Płeć</label>
                    <div className="flex gap-2">
                        {['male', 'female'].map((g) => (
                            <button
                                key={g}
                                onClick={() => setGender(g as 'male' | 'female')}
                                className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-semibold transition ${gender === g
                                    ? 'bg-brand-600 text-white'
                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
                                    }`}
                            >
                                {g === 'male' ? 'Mężczyzna' : 'Kobieta'}
                            </button>
                        ))}
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Aktywność</label>
                    <select
                        value={activity}
                        onChange={(e) => setActivity(e.target.value)}
                        className="w-full px-3 py-2.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500 transition"
                    >
                        {activityLevels.map((l) => (
                            <option key={l.value} value={l.value}>{l.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {tdee > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 animate-fade-in">
                    <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-center">
                        <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">BMR</p>
                        <p className="text-lg font-bold">{Math.round(bmr)}</p>
                        <p className="text-[9px] text-slate-400">kcal/dzień</p>
                    </div>
                    <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-950/30 border border-brand-200 dark:border-brand-800 text-center">
                        <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">TDEE</p>
                        <p className="text-lg font-bold text-brand-600 dark:text-brand-400">{Math.round(tdee)}</p>
                        <p className="text-[9px] text-slate-400">kcal/dzień</p>
                    </div>
                    <div className="p-4 rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-center">
                        <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Redukcja</p>
                        <p className="text-lg font-bold text-green-600 dark:text-green-400">{Math.round(tdee - 500)}</p>
                        <p className="text-[9px] text-slate-400">-500 kcal</p>
                    </div>
                    <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 text-center">
                        <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Masa</p>
                        <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{Math.round(tdee + 300)}</p>
                        <p className="text-[9px] text-slate-400">+300 kcal</p>
                    </div>
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════
// 1RM Calculator (Epley + Brzycki)
// ══════════════════════════════════════════
function OneRMCalculator() {
    const [weight, setWeight] = useState('');
    const [reps, setReps] = useState('');

    const w = parseFloat(weight);
    const r = parseFloat(reps);

    let epley = 0;
    let brzycki = 0;
    if (w > 0 && r > 0 && r <= 30) {
        epley = r === 1 ? w : w * (1 + r / 30);
        brzycki = r === 1 ? w : w * (36 / (37 - r));
    }
    const avg = (epley + brzycki) / 2;

    const percentages = [100, 95, 90, 85, 80, 75, 70, 65, 60];

    return (
        <div>
            <h3 className="font-bold text-base mb-1">Kalkulator 1RM</h3>
            <p className="text-xs text-slate-400 mb-4">Szacowany maksymalny ciężar na 1 powtórzenie (Epley / Brzycki)</p>

            <div className="grid grid-cols-2 gap-4 mb-6">
                <Input label="Podniesiony ciężar" value={weight} onChange={setWeight} min={1} max={600} unit="kg" placeholder="100" />
                <Input label="Liczba powtórzeń" value={reps} onChange={setReps} min={1} max={30} placeholder="5" />
            </div>

            {avg > 0 && (
                <div className="animate-fade-in space-y-4">
                    <div className="grid grid-cols-3 gap-3">
                        <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-950/30 border border-brand-200 dark:border-brand-800 text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Epley</p>
                            <p className="text-lg font-bold text-brand-600 dark:text-brand-400">{epley.toFixed(1)} kg</p>
                        </div>
                        <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Brzycki</p>
                            <p className="text-lg font-bold text-purple-600 dark:text-purple-400">{brzycki.toFixed(1)} kg</p>
                        </div>
                        <div className="p-4 rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Średnia</p>
                            <p className="text-lg font-bold text-green-600 dark:text-green-400">{avg.toFixed(1)} kg</p>
                        </div>
                    </div>

                    <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Tabela procentowa</p>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="text-[10px] font-semibold text-slate-400 uppercase">
                                        <th className="text-left p-2">%1RM</th>
                                        <th className="text-center p-2">Ciężar</th>
                                        <th className="text-center p-2">~Powtórzenia</th>
                                        <th className="text-left p-2">Zastosowanie</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {percentages.map((pct) => {
                                        const load = avg * pct / 100;
                                        const estReps = pct === 100 ? 1 : Math.round(30 * (1 - pct / 100) / (1 - 1 / 1.0333));
                                        const zone = pct >= 90 ? 'Siła max.' : pct >= 80 ? 'Siła' : pct >= 70 ? 'Hipertrofia' : 'Wytrzymałość';
                                        return (
                                            <tr key={pct} className="border-t border-slate-100 dark:border-slate-800">
                                                <td className="p-2 font-medium">{pct}%</td>
                                                <td className="p-2 text-center">
                                                    <span className="px-2 py-0.5 rounded bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300 text-xs font-bold">
                                                        {load.toFixed(1)} kg
                                                    </span>
                                                </td>
                                                <td className="p-2 text-center text-xs text-slate-500">
                                                    {pct === 100 ? '1' : pct >= 95 ? '1-2' : pct >= 90 ? '3-4' : pct >= 85 ? '4-6' : pct >= 80 ? '6-8' : pct >= 75 ? '8-10' : pct >= 70 ? '10-12' : pct >= 65 ? '12-15' : '15-20'}
                                                </td>
                                                <td className="p-2 text-xs text-slate-400">{zone}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════
// Macro Calculator
// ══════════════════════════════════════════
function MacroCalculator() {
    const [tdee, setTdee] = useState('');
    const [goal, setGoal] = useState<'cut' | 'maintain' | 'bulk'>('maintain');
    const [weight, setWeight] = useState('');

    const t = parseFloat(tdee);
    const w = parseFloat(weight);

    const adjustedTdee = goal === 'cut' ? t - 500 : goal === 'bulk' ? t + 300 : t;

    // Protein: 2g/kg for cut, 1.8g/kg for maintain, 1.6g/kg for bulk
    const proteinPerKg = goal === 'cut' ? 2.2 : goal === 'bulk' ? 1.8 : 2.0;
    const protein = w > 0 ? Math.round(w * proteinPerKg) : 0;
    const proteinCal = protein * 4;

    // Fat: 25% of total calories
    const fatCal = adjustedTdee * 0.25;
    const fat = Math.round(fatCal / 9);

    // Carbs: remaining calories
    const carbsCal = adjustedTdee - proteinCal - fatCal;
    const carbs = Math.round(carbsCal / 4);

    const valid = t > 0 && w > 0 && adjustedTdee > 0;

    return (
        <div>
            <h3 className="font-bold text-base mb-1">Kalkulator makroskładników</h3>
            <p className="text-xs text-slate-400 mb-4">Rozkład białka, węglowodanów i tłuszczu na podstawie TDEE</p>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <Input label="TDEE (kcal/dzień)" value={tdee} onChange={setTdee} min={1000} max={8000} placeholder="2500" />
                <Input label="Masa ciała" value={weight} onChange={setWeight} min={30} max={300} unit="kg" placeholder="80" />
            </div>

            <div className="mb-6">
                <label className="block text-xs font-medium text-slate-500 mb-1">Cel</label>
                <div className="flex gap-2">
                    {([['cut', 'Redukcja (-500)'], ['maintain', 'Utrzymanie'], ['bulk', 'Masa (+300)']] as const).map(([g, label]) => (
                        <button
                            key={g}
                            onClick={() => setGoal(g)}
                            className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-semibold transition ${goal === g
                                ? 'bg-brand-600 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {valid && (
                <div className="animate-fade-in space-y-4">
                    <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-950/30 border border-brand-200 dark:border-brand-800 text-center">
                        <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Docelowe kalorie</p>
                        <p className="text-2xl font-black text-brand-600 dark:text-brand-400">{Math.round(adjustedTdee)} kcal</p>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Białko</p>
                            <p className="text-xl font-bold text-red-600 dark:text-red-400">{protein}g</p>
                            <p className="text-[9px] text-slate-400">{proteinCal} kcal · {proteinPerKg}g/kg</p>
                        </div>
                        <div className="p-4 rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Tłuszcz</p>
                            <p className="text-xl font-bold text-amber-600 dark:text-amber-400">{fat}g</p>
                            <p className="text-[9px] text-slate-400">{Math.round(fatCal)} kcal · 25%</p>
                        </div>
                        <div className="p-4 rounded-xl bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 text-center">
                            <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Węglowodany</p>
                            <p className="text-xl font-bold text-blue-600 dark:text-blue-400">{carbs > 0 ? carbs : 0}g</p>
                            <p className="text-[9px] text-slate-400">{Math.round(carbsCal > 0 ? carbsCal : 0)} kcal</p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ══════════════════════════════════════════
// Wilks Score Calculator
// ══════════════════════════════════════════
function WilksCalculator() {
    const [bodyweight, setBodyweight] = useState('');
    const [total, setTotal] = useState('');
    const [benchPress, setBenchPress] = useState('');
    const [gender, setGender] = useState<'male' | 'female'>('male');

    const bw = parseFloat(bodyweight);
    const t = parseFloat(total);
    const bp = parseFloat(benchPress);

    // Wilks coefficients
    const coeff = gender === 'male'
        ? { a: -216.0475144, b: 16.2606339, c: -0.002388645, d: -0.00113732, e: 7.01863e-06, f: -1.291e-08 }
        : { a: 594.31747775582, b: -27.23842536447, c: 0.82112226871, d: -0.00930733913, e: 4.731582e-05, f: -9.054e-08 };

    let wilks = 0;
    let wilksBench = 0;
    if (bw > 0 && t > 0) {
        const x = bw;
        const denominator = coeff.a + coeff.b * x + coeff.c * x ** 2 + coeff.d * x ** 3 + coeff.e * x ** 4 + coeff.f * x ** 5;
        wilks = denominator !== 0 ? t * (500 / denominator) : 0;
    }

    if (bw > 0 && bp > 0) {
        const x = bw;
        const denominator = coeff.a + coeff.b * x + coeff.c * x ** 2 + coeff.d * x ** 3 + coeff.e * x ** 4 + coeff.f * x ** 5;
        wilksBench = denominator !== 0 ? bp * (500 / denominator) : 0;
    }

    const getRating = (w: number, isMale: boolean): string => {
        if (isMale) {
            if (w < 200) return 'Początkujący';
            if (w < 300) return 'Średniozaawansowany';
            if (w < 400) return 'Zaawansowany';
            if (w < 500) return 'Elita';
            return 'Światowa klasa';
        } else {
            if (w < 150) return 'Początkujący';
            if (w < 250) return 'Średniozaawansowany';
            if (w < 350) return 'Zaawansowany';
            if (w < 450) return 'Elita';
            return 'Światowa klasa';
        }
    };

    const getBenchRating = (w: number, isMale: boolean): string => {
        // Bench-only Wilks score is much lower than full S+B+D total Wilks.
        if (isMale) {
            if (w < 70) return 'Początkujący';
            if (w < 90) return 'Średniozaawansowany';
            if (w < 110) return 'Zaawansowany';
            if (w < 130) return 'Elita';
            return 'Światowa klasa';
        }

        if (w < 45) return 'Początkujący';
        if (w < 60) return 'Średniozaawansowany';
        if (w < 75) return 'Zaawansowany';
        if (w < 90) return 'Elita';
        return 'Światowa klasa';
    };

    return (
        <div>
            <h3 className="font-bold text-base mb-1">Kalkulator Wilks</h3>
            <p className="text-xs text-slate-400 mb-4">Wynik siły względnej dla totalu oraz samego wyciskania na ławce</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                <Input label="Masa ciała" value={bodyweight} onChange={setBodyweight} min={30} max={300} unit="kg" placeholder="80" />
                <Input label="Total (S+B+D)" value={total} onChange={setTotal} min={1} max={1500} unit="kg" placeholder="500" />
                <Input label="Wyciskanie leżąc" value={benchPress} onChange={setBenchPress} min={1} max={500} unit="kg" placeholder="140" />
            </div>

            <div className="mb-6">
                <label className="block text-xs font-medium text-slate-500 mb-1">Płeć</label>
                <div className="flex gap-2">
                    {['male', 'female'].map((g) => (
                        <button
                            key={g}
                            onClick={() => setGender(g as 'male' | 'female')}
                            className={`flex-1 px-3 py-2.5 rounded-lg text-xs font-semibold transition ${gender === g
                                ? 'bg-brand-600 text-white'
                                : 'bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700'
                                }`}
                        >
                            {g === 'male' ? 'Mężczyzna' : 'Kobieta'}
                        </button>
                    ))}
                </div>
            </div>

            {(wilks > 0 || wilksBench > 0) && (
                <div className="space-y-4 animate-fade-in">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {wilks > 0 && (
                            <div className="p-4 rounded-xl bg-brand-50 dark:bg-brand-950/30 border border-brand-200 dark:border-brand-800 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Wynik Wilks (Total)</p>
                                <p className="text-2xl font-black text-brand-600 dark:text-brand-400">{wilks.toFixed(1)}</p>
                            </div>
                        )}

                        {wilksBench > 0 && (
                            <div className="p-4 rounded-xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800 text-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Wynik Wilks Bench</p>
                                <p className="text-2xl font-black text-purple-600 dark:text-purple-400">{wilksBench.toFixed(1)}</p>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {wilks > 0 && (
                            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-center flex flex-col justify-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Poziom (Total)</p>
                                <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                    {getRating(wilks, gender === 'male')}
                                </p>
                            </div>
                        )}

                        {wilksBench > 0 && (
                            <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-center flex flex-col justify-center">
                                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1">Poziom (Bench)</p>
                                <p className="text-lg font-bold text-purple-600 dark:text-purple-400">
                                    {getBenchRating(wilksBench, gender === 'male')}
                                </p>
                            </div>
                        )}
                    </div>

                    <p className="text-[11px] text-slate-400">
                        Bench Wilks liczone jako: wyciskanie × współczynnik Wilks dla masy ciała.
                    </p>
                </div>
            )}
        </div>
    );
}
